import { API_URL } from "../config/env";

/**
 * Photos, videos and files in chats — Messages and Support. One per message, like
 * WhatsApp: picking several sends several messages, the caption going with the first.
 *
 * Files are private: the app shows them through each chat's own file route, with the
 * token in the address since <img> and <video> can't send headers.
 * @see mintly-back/src/utils/chatAttachments.js
 */

/** The server refuses anything bigger. */
export const MAX_CHAT_FILE_BYTES = 100 * 1024 * 1024;
/** How many can wait in the box at once. */
export const MAX_PENDING_FILES = 30;

const INLINE_IMAGES = new Set(["image/jpeg", "image/png", "image/gif", "image/webp", "image/avif", "image/bmp"]);
const INLINE_VIDEOS = new Set(["video/mp4", "video/webm", "video/quicktime", "video/ogg"]);

/** What the server will make of a file: shown in place as a photo or video, or a file to download. */
export const kindOfFile = (file) => {
  const type = String(file?.type || file?.mimeType || "").toLowerCase();
  if (INLINE_IMAGES.has(type)) return "image";
  if (INLINE_VIDEOS.has(type)) return "video";
  return "file";
};

/** An API path to a chat file, signed in as `tokenKey`'s account ("adminToken" in the admin panel). */
export const chatFileUrl = (path, { download = false, tokenKey = "token" } = {}) => {
  const params = new URLSearchParams();
  let token = null;
  try {
    token = localStorage.getItem(tokenKey);
  } catch {
    /* signed out */
  }
  if (token) params.set("access_token", token);
  if (download) params.set("download", "1");
  const qs = params.toString();
  return `${API_URL.replace(/\/$/, "")}${path}${qs ? `?${qs}` : ""}`;
};

/** "📷 Photo", "🎬 Video" or "📎 name", for a message that's only a file. */
export const attachmentLabel = (attachment, t) => {
  if (!attachment?.kind) return "";
  if (attachment.kind === "image") return `📷 ${t("chatFiles.photo")}`;
  if (attachment.kind === "video") return `🎬 ${t("chatFiles.video")}`;
  return `📎 ${attachment.name || t("chatFiles.file")}`;
};

/** A message's text, or what it sent: for chat lists, replies and previews. */
export const messagePreview = (message, t) => {
  const text = typeof message?.content === "string" ? message.content.trim() : "";
  return text || attachmentLabel(message?.attachment, t);
};

export const formatBytes = (bytes) => {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  const mb = n / 1024 / 1024;
  return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
};

/** "PDF", "DOCX"…: the extension, for a file's icon. */
export const extensionOf = (name) => {
  const m = /\.([a-z0-9]{1,6})$/i.exec(String(name || ""));
  return m ? m[1].toUpperCase() : "";
};

/** The files a drop or a paste carries, if any. */
export const filesFrom = (dataTransfer) => Array.from(dataTransfer?.files || []);
